/**
 * GitLabConnectionForm — "Add GitLab Connection".
 *
 * Registers a GitLab connection (instance + credential) so LeftShield can scan its projects.
 * `POST` via `useCreateGitLabConnection()`.
 *
 * Pattern: `react-hook-form` + `zodResolver`, schema imported from `GitLabConnection.schema.ts`
 * (never redefined), Fluent `Field` for verbatim validation messages, server errors mapped via
 * the shared `applyServerErrors` (Charter M3 / form-standard.context.md). The outer `Dialog`
 * chrome is owned by `IntegrationsPage.tsx` (this feature's own `components/`, not `src/pages/**`)
 * — this component renders the form CONTENT only and keeps its existing `onSuccess`/`onCancel`
 * prop contract so the hosting page needs no change.
 */
import type { FC } from 'react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Field, Input, Select, makeStyles, tokens } from '@fluentui/react-components';
import { PlugConnectedAddRegular } from '@fluentui/react-icons';
import { useCreateGitLabConnection } from '../hooks';
import type { CreateGitLabConnectionRequest } from '../types';
import { applyServerErrors, useReportFormDirty, FormActions, ServerErrorBanner } from '../../../components/forms';
import { GITLAB_AUTH_TYPE_OPTIONS } from './connectionFormFields';
import {
  GitLabConnectionSchema,
  type GitLabConnectionFormValues,
  type GitLabConnectionFormOutput,
} from './GitLabConnection.schema';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
});

// Known request-DTO field keys — used by the server-error -> field mapping.
const KNOWN_FIELDS = ['name', 'instanceUrl', 'authType', 'accessToken'] as const;

const DEFAULT_VALUES: GitLabConnectionFormValues = {
  name: '',
  instanceUrl: 'https://gitlab.com',
  authType: 'PersonalAccessToken',
  accessToken: '',
};

export interface GitLabConnectionFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  /** Reports dirty state up so the hosting dialog can guard an unsaved-changes dismiss. */
  onDirtyChange?: (dirty: boolean) => void;
}

export const GitLabConnectionForm: FC<GitLabConnectionFormProps> = ({ onSuccess, onCancel, onDirtyChange }) => {
  const styles = useStyles();

  const {
    control,
    handleSubmit,
    setError,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<GitLabConnectionFormValues, unknown, GitLabConnectionFormOutput>({
    resolver: zodResolver(GitLabConnectionSchema),
    mode: 'onTouched',
    defaultValues: DEFAULT_VALUES,
  });

  useReportFormDirty(isDirty, onDirtyChange);

  const authType = watch('authType');

  const [globalError, setGlobalError] = useState<string | undefined>();

  const createConnection = useCreateGitLabConnection();

  const onValid = (values: GitLabConnectionFormOutput) => {
    // Guard double-submit.
    if (createConnection.isPending) return;
    setGlobalError(undefined);

    const request: CreateGitLabConnectionRequest = {
      name: values.name,
      instanceUrl: values.instanceUrl,
      authType: values.authType,
      accessToken: values.accessToken,
    };

    createConnection.mutate(request, {
      onSuccess: () => {
        reset(DEFAULT_VALUES);
        onSuccess?.();
      },
      onError: (error) => {
        const { globalMessage } = applyServerErrors(error, KNOWN_FIELDS, setError);
        setGlobalError(globalMessage);
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onValid)} className={styles.form} noValidate>
      <ServerErrorBanner title="Error" message={globalError} testId="gitlab-connection-form-server-error" />

      <Field
        label="Connection Name"
        required
        validationState={errors.name ? 'error' : undefined}
        validationMessage={errors.name?.message}
      >
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="gitlab-connection-form-name"
              value={field.value ?? ''}
              placeholder="My GitLab Instance"
              disabled={createConnection.isPending}
            />
          )}
        />
      </Field>

      <Field
        label="Instance URL"
        required
        validationState={errors.instanceUrl ? 'error' : undefined}
        validationMessage={errors.instanceUrl?.message}
        hint="For GitLab.com, use https://gitlab.com. For self-hosted, enter your instance URL."
      >
        <Controller
          name="instanceUrl"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="gitlab-connection-form-instanceUrl"
              value={field.value ?? ''}
              placeholder="https://gitlab.com"
              disabled={createConnection.isPending}
            />
          )}
        />
      </Field>

      <Field label="Authentication Type" required>
        <Controller
          name="authType"
          control={control}
          render={({ field }) => (
            <Select
              value={field.value}
              onChange={(_, data) => field.onChange(data.value)}
              onBlur={field.onBlur}
              disabled={createConnection.isPending}
              data-testid="gitlab-connection-form-authType"
            >
              {GITLAB_AUTH_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          )}
        />
      </Field>

      <Field
        label={authType === 'PersonalAccessToken' ? 'Personal Access Token' : 'Access Token'}
        required
        validationState={errors.accessToken ? 'error' : undefined}
        validationMessage={errors.accessToken?.message}
        hint="Token requires api scope for full functionality. read_api for read-only access."
      >
        <Controller
          name="accessToken"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              type="password"
              data-testid="gitlab-connection-form-accessToken"
              value={field.value ?? ''}
              placeholder="glpat-xxxxxxxxxxxxxxxxxxxx"
              disabled={createConnection.isPending}
            />
          )}
        />
      </Field>

      <FormActions
        mode="create"
        testIdPrefix="gitlab-connection-form"
        isSubmitting={createConnection.isPending}
        onCancel={onCancel}
        submitLabel="Create Connection"
        submittingLabel="Creating…"
        submitIcon={<PlugConnectedAddRegular />}
      />
    </form>
  );
};
